"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    ADOUCISSEMENT,
    Avancer,
    ChampDate,
    Curseur,
    Ecran,
    Question,
    Recommencer,
    Resultat,
    Segments,
    dateLisible,
    euros,
    motion,
    pourcent,
    useMontants,
    useParcours,
} from "./Journey";
import { economieAcre, delaiDemandeOuvert, reprisePrecoce } from "@/lib/tools/incomeTax";
import { expliquerAcre } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * ACRE : y ai-je droit, combien j'économise, et jusqu'à quand.
 *
 * UNE QUESTION PAR ÉCRAN, et chaque question se répond sans réfléchir. La
 * première version faisait lire une liste de neuf catégories puis demandait un
 * oui global : c'était déporter sur le lecteur le travail de se reconnaître
 * dedans, précisément ce qu'il ne sait pas faire.
 *
 * Le parcours s'ouvre donc sur la question la plus facile du monde, « où en
 * est votre micro-entreprise ? », puis passe les catégories une par une
 * et s'arrête à la première qui répond. Quelqu'un au chômage indemnisé, le cas
 * de très loin le plus fréquent, voit cinq écrans en tout.
 *
 * LA DATE N'EST DEMANDÉE QU'UNE FOIS, juste après, parce que c'est là qu'elle a
 * un sens : elle décide à la fois du délai de soixante jours, du régime
 * d'exonération et de la date de fin. Le champ répond en direct, « il vous
 * reste 24 jours » ou « le délai a expiré il y a 68 jours ».
 *
 * TROIS PORTES PEUVENT SE FERMER, et le résultat dit LAQUELLE :
 *   1. ne relever d'aucune catégorie ouvrant droit ;
 *   2. avoir déjà touché l'ACRE dans les trois années précédentes ;
 *   3. avoir déclaré son activité il y a plus de soixante jours, auquel cas le
 *      droit est perdu même si tout le reste est réuni.
 *
 * La troisième est la plus injuste et la plus fréquente, puisqu'elle ne tient
 * qu'à une date, et c'est celle qu'un simulateur oublie le plus facilement.
 *
 * C'est ici que le format « parcours » s'est réglé ; ses briques (écrans,
 * animations, mécanique de retour) vivent maintenant dans Parcours.jsx et
 * sont partagées par tous les outils.
 */

/* Plafonds du régime micro (référentiel plafond_micro_vente et plafond_micro_services_bnc) :
   bornes du curseur de chiffre d'affaires, comme dans le simulateur de revenu net. */
const PLAFOND_VENTE = 203100;
const PLAFOND_SERVICES = 83600;

/* Le parcours, écran par écran. Nommés plutôt que numérotés : il se ramifie. */
const PHASES = { creation: 0, date: 0, emploi: 1, minima: 1, age: 1, zone: 1, reprise: 1, anteriorite: 1, cessation: 1, dateCessation: 1, montant: 2 };

/** Une carte de réponse, dans l'ordre où on la lit : l'icône, le titre, ce qu'elle fait, la précision. */
const carte = (icone, titre, onClick, detail) => ({ icone, titre, detail, onClick });

const L = {
    fr: {
        phases: ["Votre projet", "Vos droits", "Le montant"],
        retour: "Retour",
        oui: "Oui",
        non: "Non",
        continuer: "Continuer",

        qCreation: "Où en est votre micro-entreprise ?",
        qCreationAide:
            "Tout part de là : l'ACRE se demande dans les soixante jours qui suivent le début de l'activité.",
        creationNon: "Encore en projet",
        creationNonDetail: "Rien n'est déclaré pour l'instant",
        creationOui: "Déjà déclarée",
        creationOuiDetail: "Au Guichet unique, avec une date de début d'activité",

        qDate: "Quelle est votre date de début d'activité ?",
        qDateAide:
            "Celle que vous avez déclarée au Guichet unique, pas celle de votre première facture.",
        qDatePrevue: "Quand comptez-vous démarrer ?",
        qDatePrevueAide:
            "Une date approximative suffit : elle sert à savoir de quel côté du 1er juillet 2026 vous tombez, et à poser votre échéance.",
        delaiOuvert: "Il vous reste {n} jours pour déposer la demande, jusqu'au {date}.",
        delaiOuvertUn: "Il vous reste un jour pour déposer la demande, jusqu'au {date}.",
        delaiDernierJour: "C'est aujourd'hui le dernier jour pour déposer la demande.",
        delaiForclos: "Le délai a expiré il y a {n} jours, le {date}.",
        delaiForclosUn: "Le délai a expiré il y a un jour, le {date}.",
        delaiFutur: "Vous aurez jusqu'au {date} pour déposer la demande.",
        dateRequise: "Indiquez une date pour continuer.",

        qEmploi: "Où en êtes-vous avec France Travail ?",
        qEmploiAide:
            "Toucher l'allocation chômage suffit à ouvrir le droit. Sans allocation, il faut six mois d'inscription au cours des dix-huit derniers mois.",
        emploiIndemnise: "Je touche l'allocation chômage",
        emploiIndemniseDetail: "Versée par France Travail",
        emploiNonIndemnise: "Je suis inscrit, sans allocation",
        emploiNonIndemniseDetail: "Six mois d'inscription au cours des dix-huit derniers mois",
        emploiNon: "Je ne suis pas inscrit",
        emploiNonDetail: "Ou inscrit moins de six mois, sans allocation",

        qMinima: "Touchez-vous le RSA, l'ASS ou la PreParE ?",
        qMinimaAide:
            "Le revenu de solidarité active, l'allocation de solidarité spécifique ou la prestation partagée d'éducation de l'enfant : toucher l'une des trois suffit à ouvrir le droit.",

        qAge: "Quel âge avez-vous ?",
        qAgeAide:
            "Avant 26 ans, le droit est ouvert sans autre condition. De 26 à 29 ans, il l'est si vous êtes reconnu handicapé ou sans droit à l'allocation chômage. À partir de 30 ans, l'âge ne compte plus.",
        age1825: "J'ai entre 18 et 25 ans",
        age1825Detail: "Aucune autre condition",
        age2629: "J'ai entre 26 et 29 ans",
        age2629Detail: "Reconnu handicapé, ou sans droit à l'allocation chômage",
        ageAucun: "Ni l'un ni l'autre",
        ageAucunDetail: "30 ans ou plus, ou de 26 à 29 ans avec des droits au chômage et sans handicap reconnu",

        qZone: "L'adresse de votre entreprise est-elle dans un quartier prioritaire ou une zone France ruralités revitalisation ?",
        qZoneAide:
            "Quartier prioritaire de la politique de la ville, ou zone France ruralités revitalisation, ZFRR ou ZFRR+.",
        jeNeSaisPas: "Je ne sais pas",

        qReprise: "Reprenez-vous une entreprise en difficulté, ou êtes-vous en contrat d'appui ?",
        qRepriseAide:
            "Ce sont les deux dernières catégories qui ouvrent le droit : le salarié qui reprend tout ou partie de son entreprise placée en sauvegarde, en redressement ou en liquidation, et le titulaire d'un contrat d'appui au projet d'entreprise, le Cape, signé avec une structure d'accompagnement.",
        repriseSalarie: "Je reprends une entreprise en difficulté",
        repriseSalarieDetail: "En tant que salarié, en sauvegarde, redressement ou liquidation",
        repriseCape: "Je suis en contrat d'appui",
        repriseCapeDetail: "Un Cape, signé avec une structure d'accompagnement, couveuse ou coopérative",
        repriseAucun: "Aucun des deux",
        repriseAucunDetail: "Je crée une activité neuve, sans contrat d'appui",

        qAnteriorite: "Avez-vous eu une ACRE qui s'est terminée il y a moins de trois ans ?",
        qAnterioriteAide:
            "Pour une précédente création ou reprise, quel qu'ait été son sort. Les trois ans se comptent depuis la fin de votre précédente exonération, pas depuis la date de création.",
        anterioriteNon: "Non, aucune ACRE récente",
        anterioriteNonDetail: "Jamais, ou une exonération terminée depuis plus de trois ans",
        anterioriteOui: "Oui, il y a moins de trois ans",
        anterioriteOuiDetail: "Une exonération terminée il y a moins de trois ans, même si l'activité s'est arrêtée",

        qMontant: "Dernière étape : votre activité",
        qMontantAide:
            "Le droit est déterminé. Ces deux informations ne servent qu'à chiffrer ce que l'ACRE vous fait gagner.",
        activite: "Activité",
        vente: "Vente de marchandises",
        services: "Prestations de services (BIC)",
        bnc: "Activité libérale (BNC)",
        cipav: "Profession libérale réglementée (Cipav)",
        ca: "Chiffre d'affaires attendu sur la période aidée",
        caAide: "L'exonération s'épuise à date fixe, même si vous n'encaissez rien.",
        calculer: "Voir mon résultat",

        resultatTitre: "Votre ACRE",
        droitTitre: "Vous y avez droit",
        pasDroitTitre: "Vous n'y avez pas droit",
        motifCategorie: "Vous ne relevez d'aucune des catégories ouvrant droit à l'aide.",
        motifCategorieSuite:
            "L'ACRE suppose de relever d'une des catégories de l'article L5141-1 du code du travail, ou de créer en zone France ruralités revitalisation, ZFRR ou ZFRR+. Sans cela, le taux plein s'applique dès le premier euro encaissé.",
        motifCategorieZone:
            "Vérifiez tout de même l'adresse de votre entreprise : les quartiers prioritaires et les zones de revitalisation se consultent commune par commune, et cette seule condition suffirait à ouvrir le droit.",
        motifAnteriorite: "Votre précédente ACRE est trop récente.",
        motifAnterioriteSuite:
            "La loi impose trois ans entre la fin d'une ACRE et la suivante, même si l'activité d'avant s'est arrêtée. Les trois ans se comptent de date à date depuis la fin de votre précédente exonération, et le droit rouvrira ensuite.",
        qCessation: "Aviez-vous une activité indépendante que vous avez cessée ?",
        qCessationAide:
            "Cette règle est distincte de la précédente et vaut même si vous n'avez jamais eu l'ACRE. Une reprise d'activité intervenue dans l'année de la cessation, ou dans l'année suivante, n'est pas un début d'activité aux yeux du code de la sécurité sociale. Or l'ACRE est une exonération de début d'activité.",
        cessationNon: "Non, c'est ma première activité",
        cessationNonDetail: "Ou la précédente s'est arrêtée il y a longtemps",
        cessationOui: "Oui, j'en ai cessé une",
        cessationOuiDetail: "Quelle que soit son activité, et même si elle n'a jamais rien encaissé",

        qDateCessation: "Quand cette activité a-t-elle cessé ?",
        qDateCessationAide:
            "Seule l'année compte : celle de la cessation et celle qui la suit sont fermées, quel que soit le jour.",
        cessationFerme: "Une cessation en {annee} ferme l'ACRE jusqu'au 31 décembre {fin}. Votre reprise du {debut} tombe dans cette période.",
        cessationOuverte: "Une cessation en {annee} ferme l'ACRE jusqu'au 31 décembre {fin}. Votre reprise du {debut} est postérieure : la règle ne vous atteint pas.",

        motifCessation: "Votre reprise n'est pas un début d'activité.",
        motifCessationSuite:
            "L'article R. 131-3 du code de la sécurité sociale écarte du début d'activité toute reprise intervenue dans l'année de la cessation ou dans l'année suivante. L'ACRE étant une exonération de début d'activité, elle n'est pas due, même à qui n'en a jamais bénéficié. Le droit rouvre le 1er janvier {rouvre}.",

        motifDelai: "Le délai de demande est dépassé.",
        motifDelaiSuite:
            "La demande se dépose dans les soixante jours suivant le début d'activité déclaré, et depuis le 1er janvier 2026 elle est obligatoire pour tout le monde. Passé ce délai, le droit est perdu même lorsque toutes les autres conditions sont réunies.",
        delaiDepasseDe: "Votre délai a expiré il y a {n} jours, le {date}.",
        delaiDepasseDeUn: "Votre délai a expiré il y a un jour, le {date}.",

        avec: "Cotisations avec l'ACRE",
        sans: "Sans l'ACRE",
        economie: "Vous économisez",
        exonere: "soit {part} de vos cotisations",
        finLe: "L'exonération court jusqu'au {date}",
        finAide:
            "Fin du troisième trimestre civil qui suit celui de votre début d'activité : de neuf à douze mois selon la date. Un début au 1er janvier et un début au 31 mars finissent le même jour : mieux vaut démarrer en début de trimestre.",
        demanderAvant: "Demande à déposer avant le {date}",
        demanderAideRestant:
            "Il vous reste {n} jours. Sans réponse de l'URSSAF sous un mois, l'ACRE est présumée acceptée.",
        demanderAideRestantUn:
            "Il vous reste un jour. Sans réponse de l'URSSAF sous un mois, l'ACRE est présumée acceptée.",
        demanderAideDernierJour:
            "C'est aujourd'hui le dernier jour pour déposer la demande. Sans réponse de l'URSSAF sous un mois, l'ACRE est présumée acceptée.",
        demanderAideFutur: "Le compte à rebours démarrera à votre déclaration de début d'activité.",
        reformeAvant:
            "Votre activité a commencé avant le 1er juillet 2026 : c'est l'ancien barème qui s'applique, le plus favorable des deux.",
        reformeApres:
            "Votre activité commence à partir du 1er juillet 2026, date à laquelle l'ACRE a été revue à la baisse. C'est le nouveau barème qui s'applique, moins favorable que celui d'avant cette date.",
        recommencer: "Refaire la simulation",
        note: "L'ACRE n'exonère ni la contribution à la formation professionnelle, ni la taxe de chambre consulaire, ni l'impôt sur le revenu. Elle est sans effet sur vos droits à la retraite, calculés sur le forfait qui aurait été dû sans elle, à condition d'être à jour de vos cotisations : un impayé, en revanche, vous en fait perdre.",
    },
    en: {
        phases: ["Your project", "Your entitlement", "The amount"],
        retour: "Back",
        oui: "Yes",
        non: "No",
        continuer: "Continue",

        qCreation: "Where does your business stand?",
        qCreationAide:
            "Everything starts there: ACRE must be applied for within sixty days of the start of activity.",
        creationNon: "Still a project",
        creationNonDetail: "Nothing is declared yet",
        creationOui: "Already declared",
        creationOuiDetail: "On the Guichet unique (the INPI portal), with a start date",

        qDate: "What is your business start date?",
        qDateAide:
            "The one you declared on the Guichet unique, not the date of your first invoice.",
        qDatePrevue: "When do you plan to start?",
        qDatePrevueAide:
            "An approximate date is enough: it tells us which side of 1 July 2026 you fall on, and sets your deadline.",
        delaiOuvert: "You have {n} days left to apply, until {date}.",
        delaiOuvertUn: "You have one day left to apply, until {date}.",
        delaiDernierJour: "Today is the last day to apply.",
        delaiForclos: "The deadline passed {n} days ago, on {date}.",
        delaiForclosUn: "The deadline passed one day ago, on {date}.",
        delaiFutur: "You will have until {date} to apply.",
        dateRequise: "Enter a date to continue.",

        qEmploi: "Where do you stand with France Travail?",
        qEmploiAide:
            "Receiving unemployment benefit is enough to qualify. Without benefit, you need six months of registration within the last eighteen months.",
        emploiIndemnise: "I receive unemployment benefit",
        emploiIndemniseDetail: "Paid by France Travail",
        emploiNonIndemnise: "I am registered, without benefit",
        emploiNonIndemniseDetail: "Six months of registration within the last eighteen months",
        emploiNon: "I am not registered",
        emploiNonDetail: "Or registered under six months, without benefit",

        qMinima: "Do you receive RSA, ASS or PreParE?",
        qMinimaAide:
            "RSA (the minimum income benefit), ASS (the allowance paid once unemployment benefit runs out) or PreParE (the parental leave benefit): receiving any of the three is enough to qualify.",

        qAge: "How old are you?",
        qAgeAide:
            "Under 26, you qualify with no other condition. From 26 to 29, you qualify if you are recognised as disabled or have no entitlement to unemployment benefit. From 30, age no longer counts.",
        age1825: "I am between 18 and 25",
        age1825Detail: "No other condition",
        age2629: "I am between 26 and 29",
        age2629Detail: "Recognised as disabled, or no entitlement to unemployment benefit",
        ageAucun: "Neither",
        ageAucunDetail: "30 or over, or 26 to 29 with benefit entitlement and no recognised disability",

        qZone: "Is your business address in a priority urban district or a France ruralités revitalisation zone?",
        qZoneAide:
            "Priority urban district (QPV), or France ruralités revitalisation zone, ZFRR or ZFRR+.",
        jeNeSaisPas: "I don't know",

        qReprise: "Are you taking over a struggling business, or on a business support contract?",
        qRepriseAide:
            "These are the last two qualifying categories: an employee taking over all or part of their company in safeguard, receivership or liquidation proceedings, and the holder of a business project support contract, the Cape, signed with a support structure.",
        repriseSalarie: "I am taking over a struggling business",
        repriseSalarieDetail: "As an employee, in safeguard, receivership or liquidation",
        repriseCape: "I am on a support contract",
        repriseCapeDetail: "A Cape, signed with a support structure, incubator or cooperative",
        repriseAucun: "Neither",
        repriseAucunDetail: "I am starting a new activity, with no support contract",

        qAnteriorite: "Did you have an ACRE that ended less than three years ago?",
        qAnterioriteAide:
            "For any previous creation or takeover, whatever became of it. The three years run from the end of your previous relief, not from the date you started.",
        anterioriteNon: "No, not that recently",
        anterioriteNonDetail: "Never, or a relief that ended more than three years ago",
        anterioriteOui: "Yes, less than three years ago",
        anterioriteOuiDetail: "A relief that ended under three years ago, even if the business has since closed",

        qMontant: "Last step: your activity",
        qMontantAide:
            "The entitlement is settled. These two only serve to work out what ACRE saves you.",
        activite: "Activity",
        vente: "Sale of goods",
        services: "Commercial services (BIC)",
        bnc: "Liberal profession (BNC)",
        cipav: "Regulated liberal profession (Cipav)",
        ca: "Expected turnover over the relief period",
        caAide: "The relief runs out on a fixed date, even if you invoice nothing.",
        calculer: "See my result",

        resultatTitre: "Your ACRE",
        droitTitre: "You are entitled",
        pasDroitTitre: "You are not entitled",
        motifCategorie: "You do not fall into any of the categories that open the right.",
        motifCategorieSuite:
            "ACRE requires falling into one of the categories listed in article L5141-1 of the French Labour Code, or setting up in a ZFRR or ZFRR+ rural revitalisation zone. Without that, the full rate applies from the first euro collected.",
        motifCategorieZone:
            "Do check the address of your business all the same: priority and revitalisation areas are listed town by town, and that condition alone would open the right.",
        motifAnteriorite: "Your previous ACRE is too recent.",
        motifAnterioriteSuite:
            "The law requires three years between the end of one ACRE and the next, even if the earlier business has closed. The three years run date to date from the end of your previous relief, and the right reopens afterwards.",
        qCessation: "Did you have a self-employed activity that you ceased?",
        qCessationAide:
            "This rule is separate from the previous one and applies even if you have never had ACRE. A restart occurring in the year of the cessation, or in the following year, is not a start of activity in the eyes of the social security code. And ACRE is an exemption for starting an activity.",
        cessationNon: "No, this is my first activity",
        cessationNonDetail: "Or the previous one ended long ago",
        cessationOui: "Yes, I ceased one",
        cessationOuiDetail: "Whatever it did, and even if it never collected anything",

        qDateCessation: "When did that activity cease?",
        qDateCessationAide:
            "Only the year matters: the year of the cessation and the one after it are closed, whatever the day.",
        cessationFerme: "A cessation in {annee} closes ACRE until 31 December {fin}. Your restart on {debut} falls inside that period.",
        cessationOuverte: "A cessation in {annee} closes ACRE until 31 December {fin}. Your restart on {debut} comes after it: the rule does not reach you.",

        motifCessation: "Your restart is not a start of activity.",
        motifCessationSuite:
            "Article R. 131-3 of the social security code excludes from the start of an activity any restart occurring in the year of the cessation or in the following year. As ACRE is an exemption for starting an activity, it is not due, even to someone who has never received it. The right reopens on 1 January {rouvre}.",

        motifDelai: "The application deadline has passed.",
        motifDelaiSuite:
            "The application must be filed within sixty days of the declared start of activity, and since 1 January 2026 it is compulsory for everyone. Past that, the right is lost even when every other condition is met.",
        delaiDepasseDe: "Your window closed {n} days ago, on {date}.",
        delaiDepasseDeUn: "Your window closed one day ago, on {date}.",

        avec: "Contributions with ACRE",
        sans: "Without ACRE",
        economie: "You save",
        exonere: "that is {part} of your contributions",
        finLe: "The relief runs until {date}",
        finAide:
            "End of the third calendar quarter following the one you started in: nine to twelve months depending on the date. A start on 1 January and a start on 31 March end on the same day, so it pays to start early in a quarter.",
        demanderAvant: "Application due before {date}",
        demanderAideRestant:
            "You have {n} days left. With no answer from URSSAF within a month, ACRE is deemed accepted.",
        demanderAideRestantUn:
            "You have one day left. With no answer from URSSAF within a month, ACRE is deemed accepted.",
        demanderAideDernierJour:
            "Today is the last day to apply. With no answer from URSSAF within a month, ACRE is deemed accepted.",
        demanderAideFutur: "The countdown starts when you declare your start of activity.",
        reformeAvant:
            "Your business started before 1 July 2026: the earlier scale applies, the more generous of the two.",
        reformeApres:
            "Your business starts on or after 1 July 2026, the date ACRE was cut back. The new scale applies, less generous than the one in force before then.",
        recommencer: "Start over",
        note: "ACRE does not cover the vocational training contribution, the chamber levy or income tax. It has no effect on your pension rights, calculated on the charge that would have been due without it, provided your contributions are paid up: an unpaid bill, on the other hand, does cost you rights.",
    },
};

/** Refus motivé : la porte qui s'est fermée, et la règle qui la ferme. */
function Refus({ t, lang, motif, delai, zoneIncertaine, reprise, onRetour, onRecommencer, sens, reduit }) {
    const textes = {
        categorie: [t.motifCategorie, t.motifCategorieSuite],
        anteriorite: [t.motifAnteriorite, t.motifAnterioriteSuite],
        cessation: [
            t.motifCessation,
            t.motifCessationSuite.replace("{rouvre}", reprise?.rouvreLe?.slice(0, 4) ?? ""),
        ],
        delai: [t.motifDelai, t.motifDelaiSuite],
    }[motif];

    return (
        <Resultat onRetour={onRetour} retour={t.retour} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="flex items-center gap-2.5 mt-2">
                <SolarIcon name="close-circle-linear" className="w-6 h-6 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="font-bold text-lg dark:text-white leading-snug">{t.pasDroitTitre}</div>
            </div>

            <p className="text-base font-semibold dark:text-white mt-4 mb-2">{textes[0]}</p>
            <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">{textes[1]}</p>

            {motif === "categorie" && zoneIncertaine && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">{t.motifCategorieZone}</p>
            )}

            {motif === "delai" && delai && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-3 mb-0">
                    {(delai.jours === -1 ? t.delaiDepasseDeUn : t.delaiDepasseDe)
                        .replace("{n}", Math.abs(delai.jours))
                        .replace("{date}", dateLisible(delai.limite, lang))}
                </p>
            )}

            <Recommencer onClick={onRecommencer} libelle={t.recommencer} />
        </Resultat>
    );
}

/**
 * Résultat chiffré.
 *
 * Les trois montants montent ensemble en quatre dixièmes de seconde : c'est
 * l'animation signature de cet écran, et la seule. Les taux, eux, restent
 * fixes : un pourcentage qui défile ne dit rien de plus, il fait du bruit.
 *
 * Le compteur vit ici, dans le composant monté à l'arrivée sur le résultat,
 * et non dans le parcours : lancé plus tôt, il aurait fini de compter avant
 * que quiconque le regarde.
 */
function ResultatChiffre({ t, lang, r, ca, activite, delai, onRetour, onRecommencer, sens, reduit }) {
    const p = useMontants(reduit);

    // Chaque montant du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(() => expliquerAcre({ chiffreAffaires: ca, activite }, r, lang), [ca, activite, r, lang]);
    const { ouverte, basculer } = useExplications();

    // Le dernier jour et la veille ont leur phrase : « 0 jours » se lit comme
    // un délai perdu, ce que la lib ne dit pas (le délai reste ouvert).
    const restant = !delai
        ? t.demanderAideFutur
        : delai.jours === 0
          ? t.demanderAideDernierJour
          : delai.jours === 1
            ? t.demanderAideRestantUn
            : t.demanderAideRestant.replace("{n}", delai.jours);

    return (
        <Resultat onRetour={onRetour} retour={t.retour} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {/* Le verdict se lit avant les chiffres, au même gabarit que le refus. */}
            <div className="flex items-center gap-2.5 mt-2">
                <SolarIcon name="check-circle-linear" className="w-6 h-6 shrink-0 text-green-700 dark:text-green-400" />
                <div className="font-bold text-lg dark:text-white leading-snug">{t.droitTitre}</div>
            </div>

            <IndiceExplication lang={lang} />

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {[
                    { cle: "avec", libelle: t.avec, montant: r.avecAide, taux: r.tauxAide, cadre: "border-primary/40 bg-primary/[0.06]" },
                    { cle: "sans", libelle: t.sans, montant: r.sansAide, taux: r.tauxPlein, cadre: "border-gray-200/70 dark:border-white/10" },
                ].map(({ cle, libelle, montant, taux, cadre }) => (
                    <div key={cle} className={`rounded-xl p-5 border ${cadre}`}>
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">{libelle}</div>
                        <div className="text-2xl font-bold tabular-nums dark:text-white">
                            <MontantExplicable explication={ex[cle]} ouvert={ouverte === cle} onBasculer={() => basculer(cle)}>
                                {euros(montant * p, lang)}
                            </MontantExplicable>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 tabular-nums">{pourcent(taux, lang)}</div>
                        {ouverte === cle && <PanneauExplication explication={ex[cle]} lang={lang} />}
                    </div>
                ))}
            </div>

            <p className="mt-5 mb-0 text-lg dark:text-white">
                {t.economie}{" "}
                <strong
                    className={`tabular-nums ${r.economie > 0 ? "text-green-700 dark:text-green-400" : "dark:text-white"}`}
                >
                    <MontantExplicable explication={ex.economie} ouvert={ouverte === "economie"} onBasculer={() => basculer("economie")}>
                        {euros(r.economie * p, lang)}
                    </MontantExplicable>
                </strong>{" "}
                <span className="text-sm text-gray-500 dark:text-lightBlue/70">
                    {t.exonere.replace("{part}", pourcent(r.partExoneree, lang, 0))}
                </span>
            </p>
            {ouverte === "economie" && <PanneauExplication explication={ex.economie} lang={lang} />}

            {/* Le vert est réservé au gain : cette ligne explique le barème,
                elle ne délivre pas un verdict. Elle ne s'allume qu'en ambre,
                quand il y a effectivement quelque chose à signaler. */}
            <p
                className={`text-sm mt-3 mb-0 ${r.regime === "avant" ? "text-gray-600 dark:text-lightBlue" : "text-amber-600 dark:text-amber-400"}`}
            >
                {r.regime === "avant" ? t.reformeAvant : t.reformeApres}
            </p>

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10 space-y-4">
                <div>
                    <div className="text-sm font-semibold dark:text-white">
                        {t.finLe.replace("{date}", dateLisible(r.fin, lang))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-0">{t.finAide}</p>
                </div>
                <div>
                    <div className="text-sm font-semibold dark:text-white">
                        {t.demanderAvant.replace("{date}", dateLisible(r.limiteDemande, lang))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1 mb-0">{restant}</p>
                </div>
            </div>

            <Recommencer onClick={onRecommencer} libelle={t.recommencer} />

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0">{t.note}</p>
        </Resultat>
    );
}

export default function SimulateurAcre({ lang = "fr", aujourdhui = null }) {
    const t = L[lang] || L.fr;
    const maintenant = useMemo(
        () => (aujourdhui ? new Date(aujourdhui) : new Date("2026-01-01T00:00:00Z")),
        [aujourdhui],
    );

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    const { valeurs: dossier } = useDossierOutil();
    const [creee, setCreee] = useState(dossier.creee ?? null);
    const [dateDebut, setDateDebut] = useState(dossier.dateDebut ?? maintenant.toISOString().slice(0, 10));
    const [situation, setSituation] = useState(null);
    const [zoneIncertaine, setZoneIncertaine] = useState(false);
    const [dejaEu, setDejaEu] = useState(null);
    // Article R. 131-3 : une reprise trop proche d'une cessation n'est pas un
    // début d'activité, et l'ACRE n'en est que l'exonération.
    const [dateCessation, setDateCessation] = useState(null);
    const [activite, setActivite] = useState(dossier.activite ?? "bnc");
    const [ca, setCa] = useState(dossier.ca ?? 15000);

    // Refaire la simulation remet les réponses à zéro, mais garde la date, l'activité
    // et le chiffre d'affaires : c'est ce qu'on corrige, pas ce qu'on ressaisit.
    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "creation",
        () => {
            setCreee(null);
            setSituation(null);
            setZoneIncertaine(false);
            setDejaEu(null);
            setDateCessation(null);
        },
        PHASES,
        reprendre([
            { etape: "creation", connu: dossier.creee !== undefined },
            // Une date connue avec un délai déjà forclos va droit au résultat,
            // comme le bouton de l'écran date. Les catégories d'ACRE ne se
            // déduisent pas du dossier : sinon le parcours s'arrête à l'emploi.
            { etape: "date", connu: dossier.dateDebut !== undefined },
            dossier.creee && dossier.dateDebut && !delaiDemandeOuvert(new Date(`${dossier.dateDebut}T00:00:00Z`), maintenant).ouvert
                ? { etape: "resultat" }
                : { etape: "emploi", connu: false },
        ]),
    );

    useSignalerDossier({ creee, dateDebut, activite, ca }, etape === "resultat");

    const debut = useMemo(() => {
        const d = new Date(`${dateDebut}T00:00:00Z`);
        return Number.isNaN(d.getTime()) ? null : d;
    }, [dateDebut]);

    const r = useMemo(
        () => (debut ? economieAcre({ chiffreAffaires: ca, activite, dateDebut: debut }) : null),
        [ca, activite, debut],
    );

    // Le délai ne court que si l'activité est déclarée : sinon il n'a pas
    // commencé, et la date saisie n'est qu'une intention.
    const delai = useMemo(
        () => (creee && debut ? delaiDemandeOuvert(debut, maintenant) : null),
        [creee, debut, maintenant],
    );

    // La reprise trop proche d'une cessation, calculée une fois pour toutes.
    const reprise = useMemo(
        () =>
            reprisePrecoce(
                dateCessation ? `${dateCessation}T00:00:00Z` : null,
                dateDebut ? `${dateDebut}T00:00:00Z` : null,
            ),
        [dateCessation, dateDebut],
    );

    const motif =
        situation === "aucune" ? "categorie"
        : dejaEu === true ? "anteriorite"
        : reprise.reprise ? "cessation"
        : delai && !delai.ouvert ? "delai"
        : null;

    /** Enregistre la catégorie trouvée et passe à la question d'antériorité. */
    const retenir = (cle) => {
        setSituation(cle);
        aller("anteriorite");
    };

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "creation") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.creation}
                titre={t.qCreation}
                aide={t.qCreationAide}
                choix={[
                    carte("lightbulb-linear", t.creationNon, () => { setCreee(false); aller("date"); }, t.creationNonDetail),
                    carte("buildings-2-linear", t.creationOui, () => { setCreee(true); aller("date"); }, t.creationOuiDetail),
                ]}
            />
        );
    }

    // Sans date valide (le navigateur propose une croix qui vide le champ), il
    // n'y a ni calcul ni délai : l'écran date se réaffiche jusqu'à ce qu'il y
    // en ait une, quel que soit l'endroit du parcours. C'est un garde-fou : le
    // bouton Continuer de cet écran ne s'affiche pas sans date, donc on ne
    // devrait jamais arriver plus loin sans elle.
    if (etape === "date" || !r) {
        // Le champ répond tout de suite : le compte à rebours est la première
        // chose que quelqu'un vient chercher, et l'apprendre à la fin du
        // parcours serait le lui apprendre trop tard.
        const delaiForclos = Boolean(creee && delai && !delai.ouvert);
        let echeance = null;
        if (creee && delai) {
            // Un seul jour a sa phrase : « 1 jours » est une faute d'accord.
            if (delai.jours < 0) {
                echeance = {
                    ton: "alerte",
                    texte: (delai.jours === -1 ? t.delaiForclosUn : t.delaiForclos)
                        .replace("{n}", Math.abs(delai.jours))
                        .replace("{date}", dateLisible(delai.limite, lang)),
                };
            } else if (delai.jours === 0) {
                echeance = { ton: "alerte", texte: t.delaiDernierJour };
            } else {
                echeance = {
                    ton: "calme",
                    texte: (delai.jours === 1 ? t.delaiOuvertUn : t.delaiOuvert)
                        .replace("{n}", delai.jours)
                        .replace("{date}", dateLisible(delai.limite, lang)),
                };
            }
        } else if (r) {
            echeance = {
                ton: "calme",
                texte: t.delaiFutur.replace("{date}", dateLisible(r.limiteDemande, lang)),
            };
        }

        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.date}
                titre={creee ? t.qDate : t.qDatePrevue}
                aide={creee ? t.qDateAide : t.qDatePrevueAide}
            >
                <ChampDate
                    valeur={dateDebut}
                    poser={setDateDebut}
                    className="w-full sm:w-72 px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] dark:text-white focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200"
                />

                {echeance && (
                    // La clé sur le texte : l'échéance se refond quand elle
                    // change, au lieu de sauter d'une phrase à l'autre.
                    <motion.p
                        key={echeance.texte}
                        initial={reduit ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2, ease: ADOUCISSEMENT }}
                        className={`text-sm mt-3 mb-0 ${
                            echeance.ton === "alerte"
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-gray-600 dark:text-lightBlue"
                        }`}
                    >
                        {echeance.texte}
                    </motion.p>
                )}

                {/*
                  Délai déjà forclos : on va droit au résultat plutôt que de
                  faire répondre cinq questions à quelqu'un dont la réponse est
                  déjà écrite. Refaire la simulation corrige une date mal saisie.
                */}
                {debut ? (
                    <Avancer onClick={() => aller(delaiForclos ? "resultat" : "emploi")}>
                        {delaiForclos ? t.calculer : t.continuer}
                    </Avancer>
                ) : (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-3 mb-0">{t.dateRequise}</p>
                )}
            </Ecran>
        );
    }

    if (etape === "emploi") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.emploi}
                titre={t.qEmploi}
                aide={t.qEmploiAide}
                choix={[
                    carte("wallet-money-linear", t.emploiIndemnise, () => retenir("are"), t.emploiIndemniseDetail),
                    carte("user-cross-rounded-linear", t.emploiNonIndemnise, () => retenir("demandeur"), t.emploiNonIndemniseDetail),
                    carte("close-circle-linear", t.emploiNon, () => aller("minima"), t.emploiNonDetail),
                ]}
            />
        );
    }

    if (etape === "minima") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.minima}
                titre={t.qMinima}
                aide={t.qMinimaAide}
                choix={[
                    carte("shield-check-linear", t.oui, () => retenir("minima")),
                    carte("close-circle-linear", t.non, () => aller("age")),
                ]}
            />
        );
    }

    if (etape === "age") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.age}
                titre={t.qAge}
                aide={t.qAgeAide}
                choix={[
                    carte("user-rounded-linear", t.age1825, () => retenir("jeune"), t.age1825Detail),
                    carte("shield-linear", t.age2629, () => retenir("jeune"), t.age2629Detail),
                    carte("close-circle-linear", t.ageAucun, () => aller("zone"), t.ageAucunDetail),
                ]}
            />
        );
    }

    if (etape === "zone") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.zone}
                titre={t.qZone}
                aide={t.qZoneAide}
                choix={[
                    carte("map-point-linear", t.oui, () => retenir("zone")),
                    carte("close-circle-linear", t.non, () => aller("reprise")),
                    carte("question-circle-linear", t.jeNeSaisPas, () => { setZoneIncertaine(true); aller("reprise"); }),
                ]}
            />
        );
    }

    if (etape === "reprise") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.reprise}
                titre={t.qReprise}
                aide={t.qRepriseAide}
                choix={[
                    carte("refresh-linear", t.repriseSalarie, () => retenir("reprise"), t.repriseSalarieDetail),
                    carte("clipboard-text-linear", t.repriseCape, () => retenir("cape"), t.repriseCapeDetail),
                    carte("close-circle-linear", t.repriseAucun, () => { setSituation("aucune"); aller("resultat"); }, t.repriseAucunDetail),
                ]}
            />
        );
    }

    if (etape === "anteriorite") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.anteriorite}
                titre={t.qAnteriorite}
                aide={t.qAnterioriteAide}
                choix={[
                    // Le délai forclos part droit au résultat depuis l'écran
                    // date, et la date ne change plus ensuite : ce test ne sert
                    // que si cette route changeait un jour.
                    carte("user-plus-linear", t.anterioriteNon, () => { setDejaEu(false); aller("cessation"); }, t.anterioriteNonDetail),
                    carte("history-linear", t.anterioriteOui, () => { setDejaEu(true); aller("resultat"); }, t.anterioriteOuiDetail),
                ]}
            />
        );
    }

    if (etape === "cessation") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.cessation}
                titre={t.qCessation}
                aide={t.qCessationAide}
                choix={[
                    carte("user-plus-linear", t.cessationNon, () => {
                        setDateCessation(null);
                        aller(delai && !delai.ouvert ? "resultat" : "montant");
                    }, t.cessationNonDetail),
                    carte("history-linear", t.cessationOui, () => {
                        setDateCessation(`${maintenant.getUTCFullYear() - 1}-12-31`);
                        aller("dateCessation");
                    }, t.cessationOuiDetail),
                ]}
            />
        );
    }

    if (etape === "dateCessation") {
        const valide = /^\d{4}-\d{2}-\d{2}$/.test(dateCessation || "");
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.dateCessation}
                titre={t.qDateCessation}
                aide={t.qDateCessationAide}
            >
                <ChampDate
                    valeur={dateCessation || ""}
                    poser={setDateCessation}
                    className="w-full sm:w-72 px-4 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white dark:bg-white/[0.04] dark:text-white focus:outline-hidden focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800 transition-colors duration-200"
                />
                {valide && (
                    <p
                        className={`text-sm mt-3 mb-0 ${
                            reprise.reprise
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-gray-600 dark:text-lightBlue"
                        }`}
                    >
                        {(reprise.reprise ? t.cessationFerme : t.cessationOuverte)
                            .replace("{annee}", reprise.anneeCessation)
                            .replace("{fin}", reprise.anneeCessation + 1)
                            .replace("{debut}", dateLisible(new Date(`${dateDebut}T00:00:00Z`), lang))}
                    </p>
                )}
                {valide && (
                    <Avancer
                        onClick={() =>
                            aller(
                                reprise.reprise || (delai && !delai.ouvert) ? "resultat" : "montant",
                            )
                        }
                    >
                        {t.continuer}
                    </Avancer>
                )}
            </Ecran>
        );
    }

    if (etape === "montant") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.montant}
                titre={t.qMontant}
                aide={t.qMontantAide}
            >
                <div className="space-y-5">
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                            {t.activite}
                        </div>
                        <Segments
                            options={["vente", "services", "bnc", "cipav"].map((cle) => ({
                                cle,
                                libelle: t[cle],
                            }))}
                            valeur={activite}
                            poser={(cle) => {
                                setActivite(cle);
                                // Le plafond de la vente est plus haut : en
                                // repassant à une autre activité, le montant
                                // saisi se ramène sous le plafond de celle-ci.
                                if (cle !== "vente") setCa((v) => Math.min(v, PLAFOND_SERVICES));
                            }}
                        />
                    </div>

                    <Curseur
                        libelle={t.ca}
                        aide={t.caAide}
                        valeur={ca}
                        poser={setCa}
                        maximum={activite === "vente" ? PLAFOND_VENTE : PLAFOND_SERVICES}
                        pas={500}
                        formater={(v) => euros(v, lang)}
                    />
                </div>

                <Avancer onClick={() => aller("resultat")} pleineLargeur>
                    {t.calculer}
                </Avancer>
            </Ecran>
        );
    }

    if (motif) {
        return (
            <Refus
                key={etape}
                t={t}
                lang={lang}
                motif={motif}
                delai={delai}
                zoneIncertaine={zoneIncertaine}
                reprise={reprise}
                onRetour={retour}
                onRecommencer={recommencer}
                {...communs}
            />
        );
    }

    return (
        <ResultatChiffre
            key={etape}
            t={t}
            lang={lang}
            r={r}
            ca={ca}
            activite={activite}
            delai={delai}
            onRetour={retour}
            onRecommencer={recommencer}
            {...communs}
        />
    );
}
